/**
 * Platform Admin Dashboard
 * Main dashboard for platform administrators showing tenant overview and metrics.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useTabbedChrome } from '@/components/weavein';
import {
  Building2,
  Users,
  CreditCard,
  TrendingUp,
  AlertCircle,
  CheckCircle2,
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  Globe,
  Mail,
  Target,
  DollarSign,
  Headphones,
  ArrowRight,
  Lock,
  Brain,
  Key,
  Webhook,
  Shield,
  Sparkles,
  Plus,
  Settings,
  BarChart3,
  RefreshCw,
} from 'lucide-react';
import { useTenant } from '@/contexts/TenantContext';
import { FeatureFlag } from '../../shared/types';
import { useRole } from '@/contexts/RoleContext';
import { apiClient } from '@/services/apiClient';
import {
  SSOSettings,
  DomainSettings,
  BrandingSettings,
  APISettings,
  WebhooksSettings,
} from '@/components/CapabilitySettings';

// Trust Care organization ID (platform operator)
const TRUST_CARE_ORG_ID = 1;
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { config as productConfig } from '@/src/config';

// ============================================================================
// TYPES
// ============================================================================

interface PlatformMetrics {
  totalTenants: number;
  activeTenants: number;
  trialTenants: number;
  totalUsers: number;
  totalContacts: number;
  mrr: number;
  arr: number;
  growthRate: number;
}

interface OrganizationSummary {
  id: string;
  slug: string;
  name: string;
  tenantType: string;
  status: string;
  plan: string;
  userCount: number;
  contactCount: number;
  createdAt: string;
}

interface SystemHealth {
  api: 'healthy' | 'degraded' | 'down';
  database: 'healthy' | 'degraded' | 'down';
  cache: 'healthy' | 'degraded' | 'down';
  email: 'healthy' | 'degraded' | 'down';
  storage: 'healthy' | 'degraded' | 'down';
  workers: 'healthy' | 'degraded' | 'down';
}

// ============================================================================
// MOCK DATA (Replace with API calls in production)
// ============================================================================

const emptyMetrics: PlatformMetrics = {
  totalTenants: 0,
  activeTenants: 0,
  trialTenants: 0,
  totalUsers: 0,
  totalContacts: 0,
  mrr: 0,
  arr: 0,
  growthRate: 0,
};

const mockSystemHealth: SystemHealth = {
  api: 'healthy',
  database: 'healthy',
  cache: 'healthy',
  email: 'healthy',
  storage: 'healthy',
  workers: 'healthy',
};

// ============================================================================
// COMPONENTS
// ============================================================================

interface MetricCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  subtitle?: string;
}

function MetricCard({ title, value, icon, trend, subtitle }: MetricCardProps) {
  const { t } = useAppTranslation();
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          {trend && (
            <div className="flex items-center mt-1">
              {trend.isPositive ? (
                <ArrowUpRight className="h-4 w-4 text-green-500" />
              ) : (
                <ArrowDownRight className="h-4 w-4 text-red-500" />
              )}
              <span
                className={`text-sm font-medium ${
                  trend.isPositive ? 'text-green-600' : 'text-red-600'
                }`}
              >
                {trend.value}%
              </span>
              <span className="text-sm text-gray-500 ml-1">{t('platform.dashboard.stats.vsLastMonth', 'vs last month')}</span>
            </div>
          )}
          {subtitle && (
            <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
          )}
        </div>
        <div className="h-12 w-12 bg-blue-50 rounded-lg flex items-center justify-center">
          {icon}
        </div>
      </div>
    </div>
  );
}

interface HealthIndicatorProps {
  name: string;
  status: 'healthy' | 'degraded' | 'down';
}

function HealthIndicator({ name, status }: HealthIndicatorProps) {
  const { t } = useAppTranslation();
  const statusConfig = {
    healthy: { icon: CheckCircle2, color: 'text-green-500', bg: 'bg-green-50', label: t('platform.dashboard.systemHealth.status.healthy', 'Healthy') },
    degraded: { icon: AlertCircle, color: 'text-yellow-500', bg: 'bg-yellow-50', label: t('platform.dashboard.systemHealth.status.degraded', 'Degraded') },
    down: { icon: AlertCircle, color: 'text-red-500', bg: 'bg-red-50', label: t('platform.dashboard.systemHealth.status.down', 'Down') },
  };

  const config = statusConfig[status];
  const Icon = config.icon;

  return (
    <div className="flex items-center space-x-2">
      <span className={`${config.bg} p-1 rounded`}>
        <Icon className={`h-4 w-4 ${config.color}`} />
      </span>
      <span className="text-sm text-gray-700">{name}:</span>
      <span className={`text-sm font-medium ${config.color}`}>
        {config.label}
      </span>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const { t } = useAppTranslation();
  const configs: Record<string, { bg: string; text: string; labelKey: string; labelDefault: string }> = {
    active: { bg: 'bg-green-100', text: 'text-green-800', labelKey: 'platform.dashboard.status.active', labelDefault: 'Active' },
    trialing: { bg: 'bg-blue-100', text: 'text-blue-800', labelKey: 'platform.dashboard.status.trial', labelDefault: 'Trial' },
    past_due: { bg: 'bg-yellow-100', text: 'text-yellow-800', labelKey: 'platform.dashboard.status.pastDue', labelDefault: 'Past Due' },
    suspended: { bg: 'bg-red-100', text: 'text-red-800', labelKey: 'platform.dashboard.status.suspended', labelDefault: 'Suspended' },
    cancelled: { bg: 'bg-gray-100', text: 'text-gray-800', labelKey: 'platform.dashboard.status.cancelled', labelDefault: 'Cancelled' },
  };

  const config = configs[status] || configs.active;

  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}
    >
      {t(config.labelKey, config.labelDefault)}
    </span>
  );
}

// ============================================================================
// MODULE CARDS WITH CAPABILITY GATING
// ============================================================================

interface ModuleCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  stats: string;
  color: string;
  feature: FeatureFlag | string;
  isEnabled: boolean;
  onClick?: () => void;
  onConfigureClick?: () => void;
}

function ModuleCard({
  icon,
  title,
  description,
  stats,
  color,
  feature,
  isEnabled,
  onClick,
  onConfigureClick,
}: ModuleCardProps) {
  const { t } = useAppTranslation();
  if (!isEnabled) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 relative overflow-hidden opacity-75">
        <div className="absolute top-0 right-0 w-20 h-20 bg-gray-100 -mr-10 -mt-10 rotate-45" />
        <div className="absolute top-3 right-3">
          <Lock className="h-4 w-4 text-gray-400" />
        </div>
        <div className="flex items-start justify-between mb-4">
          <div className={`p-3 rounded-lg ${color} opacity-50`}>
            {icon}
          </div>
        </div>
        <h3 className="font-semibold text-gray-500 mb-1">{title}</h3>
        <p className="text-sm text-gray-400 mb-3">{description}</p>
        <div className="flex items-center justify-end">
          <button
            onClick={onConfigureClick}
            className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
          >
            <Settings className="h-3 w-3" />
            {t('platform.dashboard.common.configure', 'Configure')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <button
      onClick={onClick}
      className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-lg transition-all text-left group"
    >
      <div className="flex items-start justify-between mb-4">
        <div className={`p-3 rounded-lg ${color}`}>
          {icon}
        </div>
        <ArrowRight className="h-5 w-5 text-gray-300 group-hover:text-gray-500 transition-colors" />
      </div>
      <h3 className="font-semibold text-gray-900 mb-1">{title}</h3>
      <p className="text-sm text-gray-500 mb-3">{description}</p>
      <p className="text-sm font-medium text-gray-700">{stats}</p>
    </button>
  );
}


// ============================================================================
// VIEW BILLING MODAL
// ============================================================================

interface ViewBillingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function ViewBillingModal({ isOpen, onClose }: ViewBillingModalProps) {
  const { t } = useAppTranslation();
  const billingData: { tenant: string; plan: string; mrr: number; status: string }[] = [];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px]">
        <DialogHeader>
          <DialogTitle>{t('platform.dashboard.billingModal.title', 'Billing Overview')}</DialogTitle>
          <DialogDescription>
            {t('platform.dashboard.billingModal.description', 'View subscription and billing status across tenants.')}
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-blue-50 rounded-lg p-4">
              <p className="text-sm text-blue-600">{t('platform.dashboard.billingModal.totalMrr', 'Total MRR')}</p>
              <p className="text-2xl font-bold text-blue-900">--</p>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <p className="text-sm text-green-600">{t('platform.dashboard.billingModal.activeSubscriptions', 'Active Subscriptions')}</p>
              <p className="text-2xl font-bold text-green-900">--</p>
            </div>
            <div className="bg-yellow-50 rounded-lg p-4">
              <p className="text-sm text-yellow-600">{t('platform.dashboard.billingModal.pastDue', 'Past Due')}</p>
              <p className="text-2xl font-bold text-yellow-900">--</p>
            </div>
          </div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('platform.dashboard.billingModal.columns.tenant', 'Tenant')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('platform.dashboard.billingModal.columns.plan', 'Plan')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('platform.dashboard.billingModal.columns.mrr', 'MRR')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('platform.dashboard.billingModal.columns.status', 'Status')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {billingData.map((item, idx) => (
                <tr key={idx}>
                  <td className="px-4 py-3 text-sm text-gray-900">{item.tenant}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{item.plan}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">${item.mrr}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      item.status === 'current' ? 'bg-green-100 text-green-700' :
                      item.status === 'past_due' ? 'bg-red-100 text-red-700' :
                      'bg-blue-100 text-blue-700'
                    }`}>{item.status === 'past_due' ? t('platform.dashboard.billingModal.statusPastDue', 'Past Due') : item.status === 'trial' ? t('platform.dashboard.billingModal.statusTrial', 'Trial') : t('platform.dashboard.billingModal.statusCurrent', 'Current')}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <DialogFooter>
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            {t('platform.dashboard.common.close', 'Close')}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// VIEW ANALYTICS MODAL
// ============================================================================

interface ViewAnalyticsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function ViewAnalyticsModal({ isOpen, onClose }: ViewAnalyticsModalProps) {
  const { t } = useAppTranslation();
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px]">
        <DialogHeader>
          <DialogTitle>{t('platform.dashboard.analyticsModal.title', 'Platform Analytics')}</DialogTitle>
          <DialogDescription>
            {t('platform.dashboard.analyticsModal.description', 'Overview of platform-wide metrics and trends.')}
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-600">{t('platform.dashboard.analyticsModal.weeklyActiveUsers', 'Weekly Active Users')}</p>
              <p className="text-2xl font-bold text-gray-900">1,842</p>
              <p className="text-xs text-green-600">{t('platform.dashboard.analyticsModal.vsLastWeek', '+12% vs last week')}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-600">{t('platform.dashboard.analyticsModal.apiRequests', 'API Requests (24h)')}</p>
              <p className="text-2xl font-bold text-gray-900">284K</p>
              <p className="text-xs text-green-600">{t('platform.dashboard.analyticsModal.vsYesterday', '+8% vs yesterday')}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-600">{t('platform.dashboard.analyticsModal.newSignups', 'New Signups (30d)')}</p>
              <p className="text-2xl font-bold text-gray-900">23</p>
              <p className="text-xs text-green-600">{t('platform.dashboard.analyticsModal.signupsVsLastMonth', '+15% vs last month')}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-600">{t('platform.dashboard.analyticsModal.churnRate', 'Churn Rate')}</p>
              <p className="text-2xl font-bold text-gray-900">2.1%</p>
              <p className="text-xs text-green-600">{t('platform.dashboard.analyticsModal.churnVsLastMonth', '-0.3% vs last month')}</p>
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-3">{t('platform.dashboard.analyticsModal.featureUsage', 'Feature Usage')}</h4>
            <div className="space-y-2">
              {[
                { feature: t('platform.dashboard.analyticsModal.features.webPresence', 'Web Presence'), usage: 89 },
                { feature: t('platform.dashboard.analyticsModal.features.marketing', 'Marketing'), usage: 76 },
                { feature: t('platform.dashboard.analyticsModal.features.salesPipeline', 'Sales Pipeline'), usage: 82 },
                { feature: t('platform.dashboard.analyticsModal.features.revOps', 'RevOps'), usage: 45 },
                { feature: t('platform.dashboard.analyticsModal.features.support', 'Support'), usage: 62 },
              ].map(item => (
                <div key={item.feature} className="flex items-center gap-3">
                  <span className="w-28 text-sm text-gray-600">{item.feature}</span>
                  <div className="flex-1 bg-gray-200 rounded-full h-2">
                    <div className="bg-blue-600 rounded-full h-2" style={{ width: `${item.usage}%` }} />
                  </div>
                  <span className="w-10 text-sm text-gray-600">{item.usage}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            {t('platform.dashboard.common.close', 'Close')}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// CAPABILITY CARD
// ============================================================================

interface CapabilityCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  feature: FeatureFlag | string;
  isEnabled: boolean;
  onConfigureClick?: () => void;
}

function CapabilityCard({
  icon,
  title,
  description,
  feature,
  isEnabled,
  onConfigureClick,
}: CapabilityCardProps) {
  const { t } = useAppTranslation();
  return (
    <div className={`flex items-start gap-3 p-4 rounded-lg border ${
      isEnabled ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
    }`}>
      <div className={`p-2 rounded-lg ${isEnabled ? 'bg-green-100' : 'bg-gray-100'}`}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h4 className={`font-medium ${isEnabled ? 'text-gray-900' : 'text-gray-500'}`}>
            {title}
          </h4>
          {isEnabled ? (
            <CheckCircle2 className="h-4 w-4 text-green-500" />
          ) : (
            <Lock className="h-3.5 w-3.5 text-gray-400" />
          )}
        </div>
        <p className="text-sm text-gray-500 mt-0.5">{description}</p>
        {onConfigureClick && (
          <button
            onClick={onConfigureClick}
            className="text-xs text-blue-600 hover:text-blue-700 mt-2 flex items-center gap-1"
          >
            <Settings className="h-3 w-3" />
            {t('platform.dashboard.common.configure', 'Configure')}
          </button>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

interface PlatformDashboardProps {
  onNavigate?: (tab: string) => void;
}

export function PlatformDashboard({ onNavigate }: PlatformDashboardProps) {
  const { t } = useAppTranslation();
  const tabbed = useTabbedChrome();
  const [metrics, setMetrics] = useState<PlatformMetrics>(emptyMetrics);
  const [organizations, setOrganizations] = useState<OrganizationSummary[]>([]);
  const [systemHealth, setSystemHealth] = useState<SystemHealth>(mockSystemHealth);
  const [isLoading, setIsLoading] = useState(false);
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [orgsError, setOrgsError] = useState<string | null>(null);

  // Modal states - only keeping billing and analytics modals
  const [viewBillingOpen, setViewBillingOpen] = useState(false);
  const [viewAnalyticsOpen, setViewAnalyticsOpen] = useState(false);

  // Capability settings modal states
  const [ssoSettingsOpen, setSsoSettingsOpen] = useState(false);
  const [domainSettingsOpen, setDomainSettingsOpen] = useState(false);
  const [brandingSettingsOpen, setBrandingSettingsOpen] = useState(false);
  const [apiSettingsOpen, setApiSettingsOpen] = useState(false);
  const [webhooksSettingsOpen, setWebhooksSettingsOpen] = useState(false);

  // Get tenant context for capability checking
  const { tenant, isFeatureEnabled, isLoading: tenantLoading } = useTenant();

  // Get role context to check if current user belongs to Trust Care (platform operator)
  const { userRoleInfo } = useRole();
  const isPlatformOperator = userRoleInfo?.organizationId === TRUST_CARE_ORG_ID;

  // Fetch organizations from API
  const fetchOrganizations = useCallback(async () => {
    setOrgsLoading(true);
    setOrgsError(null);
    try {
      // Fetch organizations from the API (endpoint is /api/cbos)
      const response = await apiClient.get('/api/cbos');
      const orgsData = response.data;

      // Transform API data to OrganizationSummary format
      // Backend returns: id, name, description, status, customer_status, is_sponsor, etc.
      const transformed: OrganizationSummary[] = orgsData.map((org: any) => ({
        id: String(org.id),
        slug: org.name?.toLowerCase().replace(/[^a-z0-9]+/g, '-') || '',
        name: org.name || 'Unknown',
        tenantType: org.customer_status || (org.is_sponsor ? 'Sponsor' : 'Customer'),
        status: org.status || 'active',
        plan: org.settings?.plan || 'Standard',
        userCount: org.settings?.user_count || 0,
        contactCount: org.settings?.contact_count || 0,
        createdAt: org.created_at || new Date().toISOString(),
      }));

      setOrganizations(transformed.slice(0, 10)); // Show first 10 organizations

      // Update metrics based on real data
      setMetrics(prev => ({
        ...prev,
        totalTenants: orgsData.length,
        activeTenants: orgsData.filter((o: any) => o.status === 'active').length || orgsData.length,
        trialTenants: orgsData.filter((o: any) => o.status === 'trialing').length || 0,
      }));
    } catch (err: any) {
      console.error('Failed to fetch organizations:', err);
      setOrgsError(err.message || 'Failed to load organizations');
      // Keep mock data on error
    } finally {
      setOrgsLoading(false);
    }
  }, []);

  // Handle navigation - dispatch custom event for App.tsx to catch
  const handleModuleClick = (tabId: string) => {
    if (onNavigate) {
      onNavigate(tabId);
    } else {
      // Dispatch custom event for navigation
      window.dispatchEvent(new CustomEvent('platform-navigate', { detail: { tab: tabId } }));
    }
  };


  // Only fetch organizations for platform operator (Trust Care)
  useEffect(() => {
    if (isPlatformOperator) {
      fetchOrganizations();
    }
  }, [fetchOrganizations, isPlatformOperator]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  // Check module capabilities
  // Trust Care (platform operator) has all capabilities enabled
  // Default to enabled if no tenant context (demo mode)
  const checkFeature = (feature: FeatureFlag | string): boolean => {
    // Platform operator (Trust Care) gets all features enabled
    if (isPlatformOperator) return true;
    if (tenantLoading) return false;
    if (!tenant) return true; // Demo mode - all features enabled
    return isFeatureEnabled(feature);
  };

  // Handler for configuring capabilities - navigate to administration
  const handleConfigureClick = () => {
    handleModuleClick('cbo-management');
  };

  return (
    <div className={`p-6 max-w-7xl mx-auto${productConfig.isTriveny ? ' triveny-platform' : ''}`}>
      {/* Header */}
      <div className="mb-8">
        {!tabbed && <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2"><BarChart3 className="h-6 w-6 text-blue-600" />{t('platform.dashboard.title', 'Platform Dashboard')}</h1>}
        <p className="text-gray-600 mt-1">
          {t('platform.dashboard.description', 'Overview of your Marketing & Sales Platform')}
        </p>
      </div>

      {/* Metrics Grid - only for platform operator */}
      {isPlatformOperator && (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <MetricCard
          title={t('platform.dashboard.stats.activeOrganizations', 'Active Organizations')}
          value={formatNumber(metrics.activeTenants)}
          icon={<Building2 className="h-6 w-6 text-blue-600" />}
          trend={{ value: 8.2, isPositive: true }}
          subtitle={`${metrics.trialTenants} ${t('platform.dashboard.stats.inTrial', 'in trial')}`}
        />
        <MetricCard
          title={t('platform.dashboard.stats.totalUsers', 'Total Users')}
          value={formatNumber(metrics.totalUsers)}
          icon={<Users className="h-6 w-6 text-blue-600" />}
          trend={{ value: 12.5, isPositive: true }}
        />
        <MetricCard
          title={t('platform.dashboard.stats.mrr', 'Monthly Recurring Revenue')}
          value={formatCurrency(metrics.mrr)}
          icon={<CreditCard className="h-6 w-6 text-blue-600" />}
          trend={{ value: metrics.growthRate, isPositive: true }}
        />
        <MetricCard
          title={t('platform.dashboard.stats.arr', 'Annual Recurring Revenue')}
          value={formatCurrency(metrics.arr)}
          icon={<TrendingUp className="h-6 w-6 text-blue-600" />}
          trend={{ value: 15.3, isPositive: true }}
        />
      </div>
      )}

      {/* System Health & Recent Organizations */}
      <div className={`grid grid-cols-1 ${isPlatformOperator ? 'lg:grid-cols-3' : ''} gap-6`}>
        {/* System Health */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            {t('platform.dashboard.systemHealth.title', 'System Health')}
          </h2>
          <div className="space-y-3">
            <HealthIndicator name={t('platform.dashboard.systemHealth.api', 'API')} status={systemHealth.api} />
            <HealthIndicator name={t('platform.dashboard.systemHealth.database', 'Database')} status={systemHealth.database} />
            <HealthIndicator name={t('platform.dashboard.systemHealth.cache', 'Cache')} status={systemHealth.cache} />
            <HealthIndicator name={t('platform.dashboard.systemHealth.email', 'Email')} status={systemHealth.email} />
            <HealthIndicator name={t('platform.dashboard.systemHealth.storage', 'Storage')} status={systemHealth.storage} />
            <HealthIndicator name={t('platform.dashboard.systemHealth.workers', 'Workers')} status={systemHealth.workers} />
          </div>
          <div className="mt-4 pt-4 border-t border-gray-100">
            <p className="text-xs text-gray-500 flex items-center">
              <Clock className="h-3 w-3 mr-1" />
              {t('platform.dashboard.systemHealth.lastChecked', 'Last checked: Just now')}
            </p>
          </div>
        </div>

        {/* Recent Organizations - only for platform operator */}
        {isPlatformOperator && <div className="lg:col-span-2 bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-gray-900">
                {t('platform.dashboard.recentOrganizations.title', 'Recent Organizations')}
              </h2>
              <button
                onClick={fetchOrganizations}
                disabled={orgsLoading}
                className="p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 disabled:opacity-50"
                title="Refresh organizations"
              >
                <RefreshCw className={`h-4 w-4 ${orgsLoading ? 'animate-spin' : ''}`} />
              </button>
            </div>
            <button
              onClick={() => handleModuleClick('cbo-management')}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              {t('platform.dashboard.recentOrganizations.viewAll', 'View All')}
            </button>
          </div>
          {orgsError && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
              {orgsError} - {t('platform.dashboard.recentOrganizations.showingSampleData', 'Showing sample data')}
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('platform.dashboard.recentOrganizations.columns.organization', 'Organization')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('platform.dashboard.recentOrganizations.columns.type', 'Type')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('platform.dashboard.recentOrganizations.columns.status', 'Status')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('platform.dashboard.recentOrganizations.columns.users', 'Users')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('platform.dashboard.recentOrganizations.columns.contacts', 'Contacts')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {orgsLoading ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                      <RefreshCw className="h-5 w-5 animate-spin mx-auto mb-2" />
                      {t('platform.dashboard.recentOrganizations.loading', 'Loading organizations...')}
                    </td>
                  </tr>
                ) : organizations.map((org) => (
                  <tr
                    key={org.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => handleModuleClick('cbo-management')}
                  >
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div>
                        <div className="font-medium text-gray-900">
                          {org.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {org.slug}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {org.tenantType}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <StatusBadge status={org.status} />
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatNumber(org.userCount)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatNumber(org.contactCount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>}
      </div>

      {/* Platform Modules */}
      <div className="mt-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          {t('platform.dashboard.modules.title', 'Platform Modules')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <ModuleCard
            icon={<Globe className="h-6 w-6 text-blue-600" />}
            title={t('platform.dashboard.modules.webPresence.title', 'Web Presence')}
            description={t('platform.dashboard.modules.webPresence.description', 'Landing pages, forms, assets & SEO')}
            stats={t('platform.dashboard.modules.webPresence.stats', '4 pages • 6 forms active')}
            color="bg-blue-50"
            feature={FeatureFlag.MODULE_WEB_PRESENCE}
            isEnabled={checkFeature(FeatureFlag.MODULE_WEB_PRESENCE)}
            onClick={() => handleModuleClick('platform-web-presence')}
            onConfigureClick={handleConfigureClick}
          />
          <ModuleCard
            icon={<Mail className="h-6 w-6 text-green-600" />}
            title={t('platform.dashboard.modules.marketing.title', 'Marketing')}
            description={t('platform.dashboard.modules.marketing.description', 'Campaigns, automation & contacts')}
            stats={t('platform.dashboard.modules.marketing.stats', 'Campaigns & contacts')}
            color="bg-green-50"
            feature={FeatureFlag.MODULE_MARKETING}
            isEnabled={checkFeature(FeatureFlag.MODULE_MARKETING)}
            onClick={() => handleModuleClick('platform-marketing')}
            onConfigureClick={handleConfigureClick}
          />
          <ModuleCard
            icon={<Target className="h-6 w-6 text-purple-600" />}
            title={t('platform.dashboard.modules.salesPipeline.title', 'Sales Pipeline')}
            description={t('platform.dashboard.modules.salesPipeline.description', 'Leads, opportunities & forecasts')}
            stats={t('platform.dashboard.modules.salesPipeline.stats', 'Leads & opportunities')}
            color="bg-purple-50"
            feature={FeatureFlag.MODULE_SALES}
            isEnabled={checkFeature(FeatureFlag.MODULE_SALES)}
            onClick={() => handleModuleClick('platform-sales')}
            onConfigureClick={handleConfigureClick}
          />
          <ModuleCard
            icon={<DollarSign className="h-6 w-6 text-orange-600" />}
            title={t('platform.dashboard.modules.revOps.title', 'RevOps')}
            description={t('platform.dashboard.modules.revOps.description', 'Subscriptions, billing & analytics')}
            stats={t('platform.dashboard.modules.revOps.stats', 'Subscriptions & billing')}
            color="bg-orange-50"
            feature={FeatureFlag.MODULE_REVOPS}
            isEnabled={checkFeature(FeatureFlag.MODULE_REVOPS)}
            onClick={() => handleModuleClick('platform-revops')}
            onConfigureClick={handleConfigureClick}
          />
          <ModuleCard
            icon={<Headphones className="h-6 w-6 text-red-600" />}
            title={t('platform.dashboard.modules.customerSupport.title', 'Customer Support')}
            description={t('platform.dashboard.modules.customerSupport.description', 'Tickets, KB & health scores')}
            stats={t('platform.dashboard.modules.customerSupport.stats', 'Tickets & health scores')}
            color="bg-red-50"
            feature={FeatureFlag.MODULE_SUPPORT}
            isEnabled={checkFeature(FeatureFlag.MODULE_SUPPORT)}
            onClick={() => handleModuleClick('platform-support')}
            onConfigureClick={handleConfigureClick}
          />
          <ModuleCard
            icon={<Brain className="h-6 w-6 text-indigo-600" />}
            title={t('platform.dashboard.modules.intelligence.title', 'Intelligence')}
            description={t('platform.dashboard.modules.intelligence.description', 'Signals, opportunities & market insights')}
            stats={t('platform.dashboard.modules.intelligence.stats', 'Signals & insights')}
            color="bg-indigo-50"
            feature={FeatureFlag.MODULE_INTENT_INTELLIGENCE}
            isEnabled={checkFeature(FeatureFlag.MODULE_INTENT_INTELLIGENCE)}
            onClick={() => handleModuleClick('platform-intelligence')}
            onConfigureClick={handleConfigureClick}
          />
        </div>
      </div>

      {/* Platform Capabilities */}
      <div className="mt-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          {t('platform.dashboard.capabilities.title', 'Platform Capabilities')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <CapabilityCard
            icon={<Key className="h-5 w-5 text-blue-600" />}
            title={t('platform.dashboard.capabilities.apiAccess.title', 'API Access')}
            description={t('platform.dashboard.capabilities.apiAccess.description', 'Programmatic access to platform data')}
            feature={FeatureFlag.API_ACCESS}
            isEnabled={checkFeature(FeatureFlag.API_ACCESS)}
            onConfigureClick={() => setApiSettingsOpen(true)}
          />
          <CapabilityCard
            icon={<Webhook className="h-5 w-5 text-purple-600" />}
            title={t('platform.dashboard.capabilities.webhooks.title', 'Webhooks')}
            description={t('platform.dashboard.capabilities.webhooks.description', 'Real-time event notifications')}
            feature={FeatureFlag.WEBHOOKS}
            isEnabled={checkFeature(FeatureFlag.WEBHOOKS)}
            onConfigureClick={() => setWebhooksSettingsOpen(true)}
          />
          <CapabilityCard
            icon={<Shield className="h-5 w-5 text-green-600" />}
            title={t('platform.dashboard.capabilities.sso.title', 'Single Sign-On')}
            description={t('platform.dashboard.capabilities.sso.description', 'SAML/OAuth enterprise authentication')}
            feature={FeatureFlag.SSO}
            isEnabled={checkFeature(FeatureFlag.SSO)}
            onConfigureClick={() => setSsoSettingsOpen(true)}
          />
          <CapabilityCard
            icon={<Globe className="h-5 w-5 text-orange-600" />}
            title={t('platform.dashboard.capabilities.customDomain.title', 'Custom Domain')}
            description={t('platform.dashboard.capabilities.customDomain.description', 'Use your own domain for the platform')}
            feature={FeatureFlag.CUSTOM_DOMAIN}
            isEnabled={checkFeature(FeatureFlag.CUSTOM_DOMAIN)}
            onConfigureClick={() => setDomainSettingsOpen(true)}
          />
          <CapabilityCard
            icon={<Sparkles className="h-5 w-5 text-pink-600" />}
            title={t('platform.dashboard.capabilities.whiteLabel.title', 'White Label')}
            description={t('platform.dashboard.capabilities.whiteLabel.description', 'Complete branding customization')}
            feature={FeatureFlag.WHITE_LABEL}
            isEnabled={checkFeature(FeatureFlag.WHITE_LABEL)}
            onConfigureClick={() => setBrandingSettingsOpen(true)}
          />
          <CapabilityCard
            icon={<TrendingUp className="h-5 w-5 text-cyan-600" />}
            title={t('platform.dashboard.capabilities.advancedAnalytics.title', 'Advanced Analytics')}
            description={t('platform.dashboard.capabilities.advancedAnalytics.description', 'Deep insights and custom reports')}
            feature={FeatureFlag.ADVANCED_ANALYTICS}
            isEnabled={checkFeature(FeatureFlag.ADVANCED_ANALYTICS)}
            onConfigureClick={handleConfigureClick}
          />
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mt-8 bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          {t('platform.dashboard.quickActions.title', 'Quick Actions')}
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button
            onClick={() => handleModuleClick('cbo-management')}
            className="flex items-center justify-center px-4 py-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Building2 className="h-5 w-5 text-gray-600 mr-2" />
            <span className="text-sm font-medium text-gray-700">
              {t('platform.dashboard.quickActions.addOrganization', 'Add Organization')}
            </span>
          </button>
          <button
            onClick={() => handleModuleClick('user-management')}
            className="flex items-center justify-center px-4 py-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Users className="h-5 w-5 text-gray-600 mr-2" />
            <span className="text-sm font-medium text-gray-700">
              {t('platform.dashboard.quickActions.manageUsers', 'Manage Users')}
            </span>
          </button>
          <button
            onClick={() => setViewBillingOpen(true)}
            className="flex items-center justify-center px-4 py-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <CreditCard className="h-5 w-5 text-gray-600 mr-2" />
            <span className="text-sm font-medium text-gray-700">
              {t('platform.dashboard.quickActions.viewBilling', 'View Billing')}
            </span>
          </button>
          <button
            onClick={() => setViewAnalyticsOpen(true)}
            className="flex items-center justify-center px-4 py-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <TrendingUp className="h-5 w-5 text-gray-600 mr-2" />
            <span className="text-sm font-medium text-gray-700">
              {t('platform.dashboard.quickActions.analytics', 'Analytics')}
            </span>
          </button>
        </div>
      </div>

      {/* Modals */}
      <ViewBillingModal
        isOpen={viewBillingOpen}
        onClose={() => setViewBillingOpen(false)}
      />
      <ViewAnalyticsModal
        isOpen={viewAnalyticsOpen}
        onClose={() => setViewAnalyticsOpen(false)}
      />

      {/* Capability Settings Modals */}
      <SSOSettings
        isOpen={ssoSettingsOpen}
        onClose={() => setSsoSettingsOpen(false)}
        organizationId={userRoleInfo?.organizationId}
      />
      <DomainSettings
        isOpen={domainSettingsOpen}
        onClose={() => setDomainSettingsOpen(false)}
        organizationId={userRoleInfo?.organizationId}
      />
      <BrandingSettings
        isOpen={brandingSettingsOpen}
        onClose={() => setBrandingSettingsOpen(false)}
        organizationId={userRoleInfo?.organizationId}
      />
      <APISettings
        isOpen={apiSettingsOpen}
        onClose={() => setApiSettingsOpen(false)}
        organizationId={userRoleInfo?.organizationId}
      />
      <WebhooksSettings
        isOpen={webhooksSettingsOpen}
        onClose={() => setWebhooksSettingsOpen(false)}
        organizationId={userRoleInfo?.organizationId}
      />
    </div>
  );
}

export default PlatformDashboard;
